import * as React from "react";

/**
 * Shared submit transport for interactive blocks.
 *
 * Interactive blocks (`suggestions`, `form`, `approval`, and any `carousel`
 * whose items carry actions) all emit ONE client event when acted on - no new
 * transport per block (see `docs/rich-blocks-catalog.md` §4). Each carries a
 * `submit_to` (mirroring `form.submit_to`); on a user action the block calls
 * the single `onBlockSubmit` callback the host wires through `BlockRenderer`,
 * keyed by `block_id` + the chosen action / option id and any collected values.
 *
 * The callback flows down through context so a deeply nested interactive block
 * (e.g. a `suggestions` block inside a `carousel` inside a `grid`) reaches the
 * same host handler without prop-drilling. The host validates the payload at
 * the boundary (same two-ended validation the registry mandates); an
 * un-wired host means interactive blocks render but no-op on action.
 */

/** The event an interactive block emits when the user acts on it. */
export interface BlockSubmitEvent {
  /** The acting block's stable id (envelope `block_id`). */
  block_id: string;
  /**
   * The block's declared destination (envelope `props.submit_to`) - an opaque
   * routing token the host maps to a turn / tool / endpoint. Optional because a
   * block may rely on `block_id` alone.
   */
  submit_to?: string;
  /** The block type that emitted the event (`suggestions`, `form`, ...). */
  type: string;
  /**
   * The chosen action / option / suggestion id (e.g. the clicked chip's id, an
   * approval option's value, a form's submit action). Absent for a bare
   * value-only submit.
   */
  action?: string;
  /**
   * The single value being sent back (e.g. a suggestion chip's `value`, which
   * becomes the next user turn). For multi-field controls use `values`.
   */
  value?: string;
  /** Collected field values for multi-field controls (`form`). */
  values?: Record<string, unknown>;
}

/** The host-provided handler. */
export type BlockSubmitHandler = (event: BlockSubmitEvent) => void;

const BlockSubmitContext = React.createContext<BlockSubmitHandler | undefined>(undefined);

export interface BlockSubmitProviderProps {
  /** The host handler interactive blocks call on action. */
  onBlockSubmit?: BlockSubmitHandler;
  children: React.ReactNode;
}

/**
 * Provides the host's `onBlockSubmit` to every descendant interactive block.
 * `BlockRenderer` / `BlockList` wrap their tree in this when the host passes
 * `onBlockSubmit`, so consumers wire the handler once at the chat root.
 */
export function BlockSubmitProvider({ onBlockSubmit, children }: BlockSubmitProviderProps) {
  return (
    <BlockSubmitContext.Provider value={onBlockSubmit}>{children}</BlockSubmitContext.Provider>
  );
}

/**
 * Returns a stable emitter an interactive block calls on a user action. The
 * emitter fills in `block_id` / `submit_to` / `type` from the block, so call
 * sites pass only what changed (`{ action, value }`). No-ops safely when the
 * host did not wire a handler.
 */
export function useBlockSubmit(meta: {
  block_id: string;
  type: string;
  submit_to?: string;
}): (partial: Omit<BlockSubmitEvent, "block_id" | "type" | "submit_to">) => void {
  const handler = React.useContext(BlockSubmitContext);
  const { block_id, type, submit_to } = meta;
  return React.useCallback(
    (partial) => {
      if (!handler) return;
      handler({ block_id, type, submit_to, ...partial });
    },
    [handler, block_id, type, submit_to],
  );
}
